/* eslint-disable unicorn/no-null */
import { useFormik } from 'formik';
// mantine
import { Button, Select, TextInput, UnstyledButton } from '@mantine/core';
// components
import { processFormData } from '@newo/toolkit/bff';
import { TSkill, TSkillData } from '@newo/api/gateway';
import ModelField from '@/components/ModelField';
// utils
import validationSchema from '@/modules/Designer/components/Skills/components/EditSkillForm/EditSkillForm.schema.ts';
import { IModelType } from '@/types/common.ts';
import { DEFAULT_MODEL, RUNNER_TYPES } from '@/const';

interface EditSkillFormProps {
    skill: TSkill;
    onCancel?: () => void;
    onSubmit?: (data: TSkillData) => Promise<unknown>;
}

const EditSkillForm = ({ skill, onCancel, onSubmit }: EditSkillFormProps) => {
    const initialValues: TSkillData = {
        title: skill.title ?? '',
        idn: skill.idn,
        prompt_script: skill.prompt_script,
        runner_type: skill.runner_type,
        model: skill.model ?? DEFAULT_MODEL,
        parameters: skill.parameters,
        path: skill.path ?? ''
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (formData) => {
            const data = processFormData(formData);
            try {
                await onSubmit?.(data);
                formik.setSubmitting(false);
                onCancel?.();
            } catch {
                formik.setSubmitting(false);
            }
        }
    });
    const handleCancel = () => {
        onCancel?.();
    };

    const handleSelectChange = (fieldName = 'state_idn') => {
        return (value: string | null) => {
            if (value) {
                formik.setFieldValue(fieldName, value);
            }
        };
    };

    const handleModelChange = (model?: IModelType) => {
        formik.setFieldValue('model', model);
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                error={formik.errors.idn}
                label="Skill Idn"
                name="idn"
                placeholder="Skill Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <ModelField
                value={formik.values.model ?? null}
                onChange={handleModelChange}
            />
            <TextInput
                error={formik.errors.title}
                label="Skill title"
                name="title"
                placeholder="Skill title"
                value={formik.values.title ?? ''}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Select
                comboboxProps={{ withinPortal: false }}
                data={RUNNER_TYPES}
                error={formik.errors.runner_type}
                label="Runner type"
                name="runner_type"
                value={formik.values.runner_type}
                onChange={handleSelectChange('runner_type')}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    Update
                </Button>
            </div>
        </form>
    );
};

export default EditSkillForm;
